'use client';
import { UserRole, UserType } from '@baseball-near-you/common';
import {
  DropdownMenuSelect,
  Form,
  FormInput,
  FormLabel,
  FormPasswordInput,
  FormPhoneNumberInput,
  FormSubmitButton,
  toast,
} from '@baseball-near-you/ui';
import { useOnboardingContext } from '@bb/app/onboarding/context';
import { getCreateNewUserPayload } from '@bb/lib/auth/helpers';
import { PATHS } from '@bb/lib/constants';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useCreateUserMutation } from '@bb/lib/mutations/useCreateUserMutation';
import { useUpdateUserMutation } from '@bb/lib/mutations/useUpdateUserMutation';
import { createClient } from '@bb/lib/supabase/client';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import type { OnboardingStep } from './types';

// email, phone, password, confirm password, I am a…,

export const AccountDetailsStep = (props: {
  setCurrentStep: (step: OnboardingStep) => void;
  onStepSuccess: () => Promise<void>;
}) => {
  const supabase = createClient();
  const { setRole, setType, setEmail, setPhone, setMarketingOptIn } =
    useOnboardingContext();
  const { userProfile, userProfileLoading } = useUserProfile();
  const { updateUser } = useUpdateUserMutation();
  const { createUser } = useCreateUserMutation();

  const searchParams = useSearchParams();

  const handleSubmit = async (
    data: {
      email: string;
      phone: string;
      type: UserType;
      role: UserRole | 'Team Associate' | null;
      marketingOptIn: boolean;
    } & Partial<{ password: string; confirmPassword: string }>
  ) => {
    if (userProfile) {
      await props.onStepSuccess();
    }

    if (
      !data.password ||
      !data.confirmPassword ||
      data.password !== data.confirmPassword
    ) {
      toast.error('Passwords do not match');
      return;
    }
    const { error: signUpError, data: signUpData } = await supabase.auth.signUp(
      {
        email: data.email,
        password: data.password,
      }
    );
    if (signUpError) {
      // TODO weird bug where if they dont verify they also cant login since
      // TODO we aren't creating the user until the next step TBD
      toast.error(
        <div>
          <p className="text-lg">{signUpError.message}</p>
          {signUpError.message.includes('already registered') && (
            <Link href={PATHS.Login} className="underline">
              If you already have an account, please sign in by clicking here.
            </Link>
          )}
        </div>,
        { duration: 8000 }
      );
      return;
    }

    setRole(data.role as UserRole | 'Team Associate');
    setType(data.type);
    setEmail(data.email);
    setPhone(data.phone);
    setMarketingOptIn(data.marketingOptIn);

    if (!signUpData.user) {
      console.error('NO USER IN ONBOARDING SUPABASE SIGNUP DATA');
      return;
    }

    const userPayload = getCreateNewUserPayload({
      id: signUpData.user.id,
      email: data.email,
      phone: data.phone,
      type: data.type,
      role: data.role as UserRole | 'Team Associate',
      marketingOptIn: data.marketingOptIn,
      emailVerified: null,
      image: null,
    });
    const user = await createUser(userPayload);

    posthog.identify(user.id, {
      user_id: user.id,
      user_createdAt: user.createdAt,
      user_firstName: user.firstName,
      user_lastName: user.lastName,
      user_email: user.email,
      user_phone: user.phoneNumber,
    });

    if (user) {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (signInError) {
        toast.error(signInError.message);
        return;
      }

      await props.onStepSuccess();
    }
  };

  const schema = useMemo(() => {
    return !userProfile
      ? z.object({
          email: z.string().email(),
          phone: z.string().min(10, {
            message: 'Phone number must be 10 digits',
          }),
          password: z.string().min(8, {
            message: 'Password must be at least 8 characters',
          }),
          confirmPassword: z.string().min(8, {
            message: 'Password must be at least 8 characters',
          }),
          type: z.nativeEnum(UserType),
          role: z.nativeEnum(UserRole).or(z.literal('Team Associate')),
          marketingOptIn: z.boolean(),
        })
      : z.object({
          email: z.string().email(),
          phone: z.string().min(10, {
            message: 'Phone number must be 10 digits',
          }),
          type: z.nativeEnum(UserType),
          role: z
            .nativeEnum(UserRole)
            .or(z.literal('Team Associate'))
            .nullable(),
          marketingOptIn: z.boolean(),
        });
  }, [userProfile]);

  console.log(userProfile);
  const initialValues = useMemo(() => {
    return {
      email: searchParams.get('email') ?? userProfile?.email ?? '',
      phone: userProfile?.phoneNumber ?? '',
      type: userProfile?.type ?? (null as any),
      role: (!!userProfile?.role &&
      userProfile?.role !== UserRole.Athlete &&
      userProfile?.role !== UserRole.Parent
        ? 'Team Associate'
        : (userProfile?.role ?? (null as any))) as
        | UserRole
        | 'Team Associate'
        | null,
      ...(userProfile ? {} : { password: '', confirmPassword: '' }),
      marketingOptIn: !!searchParams.get('email'),
    };
  }, [userProfile]);

  return (
    <Form
      onSubmit={handleSubmit}
      schema={schema}
      initialValues={initialValues}
      className="space-y-3.5 md:space-y-5"
    >
      <FormInput name="email" label="Email" />
      <FormPhoneNumberInput name="phone" label="Phone" />
      {!userProfile && (
        <>
          <FormPasswordInput name="password" label="Password" />
          <FormPasswordInput name="confirmPassword" label="Confirm Password" />
        </>
      )}
      <UserTypeSelect
        onSelect={(val) => {
          console.log(val);
        }}
      />
      <MarketingOptInCheckbox />
      <FormSubmitButton label={userProfile ? 'Continue' : 'Register now'} />
    </Form>
  );
};

export function MarketingOptInCheckbox() {
  const { watch, setValue } = useFormContext();
  const marketingOptIn = watch('marketingOptIn');
  return (
    <div className="flex items-center justify-start">
      <input
        id="marketingOptIn"
        name="marketingOptIn"
        type="checkbox"
        className="mr-3 mb-0.5 size-5"
        checked={marketingOptIn}
        onChange={(e) => setValue('marketingOptIn', e.target.checked)}
      />
      <FormLabel
        errorMessage={''}
        name="marketingOptIn"
        label="Opt in to marketing"
        className="w-full text-base"
      />
    </div>
  );
}

export function UserTypeSelect(props: {
  onSelect: (val: UserType) => void;
}) {
  const {
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const userType = watch('type');
  const userRole = watch('role');

  return (
    <div>
      <FormLabel
        name="role"
        label="Choose your account type"
        errorMessage={''}
        className="mb-0.5!"
      />
      <DropdownMenuSelect
        onSelect={(val) => {
          if (val === 'Team Associate') {
            setValue('role', 'Team Associate');
            setValue('type', UserType.Organization);
          } else {
            setValue('role', val);
            setValue('type', UserType.Personal);
          }
          console.log(val);
        }}
        val={userRole}
        values={[
          { label: 'Athlete', value: UserRole.Athlete },
          { label: 'Parent', value: UserRole.Parent },
          { label: 'Team Associate', value: 'Team Associate' },
        ]}
        defaultLabel="I am a..."
      />
    </div>
  );
}
